import React, { useEffect, useRef } from 'react';

import { TextClip } from '../../types/editor';
import { TEXT_FONT_FAMILIES } from './migrateTimeline';

const CANVAS_W = 800;
const CANVAS_H = 130;

interface TextStylePreviewProps {
  clip: TextClip;
}

/**
 * Live style swatch for the text-clip inspector: draws the clip's text
 * with the exact same routine the main preview canvas uses (font,
 * fill/outline, background box + corner radius, glyph V-offset), auto-
 * scaled to fit. Placement/zoom aren't shown here — that's the framing
 * column + main preview's job.
 */
export default function TextStylePreview({ clip }: TextStylePreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    let cancelled = false;
    const draw = (): void => {
      if (cancelled) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      const text = (clip.text || '').trim() || 'Your text';
      const lines = text.split('\n');
      const family = TEXT_FONT_FAMILIES[clip.font || 'sans'] || TEXT_FONT_FAMILIES.sans;

      // Base size mirrors the render (fraction of a 1920px-tall canvas),
      // then shrinks uniformly until the whole block fits the swatch.
      let px = Math.max(8, clip.fontSize * 1920);
      const measure = (size: number): { w: number; h: number } => {
        ctx.font = `700 ${size}px ${family}`;
        let maxW = 0;
        for (const ln of lines) maxW = Math.max(maxW, ctx.measureText(ln).width);
        const pad = clip.bgColor ? size / 3 : 0;
        return {
          w: maxW + pad * 2,
          h: (lines.length - 1) * size * 1.2 + size + pad * 2,
        };
      };
      let block = measure(px);
      const fit = Math.min((CANVAS_W * 0.9) / block.w, (CANVAS_H * 0.85) / block.h, 1);
      if (fit < 1) {
        px = Math.max(8, px * fit);
        block = measure(px);
      }

      const cx = CANVAS_W / 2;
      const cy = CANVAS_H / 2;
      const lineH = px * 1.2;
      const firstY = cy - ((lines.length - 1) * lineH) / 2;

      if (clip.bgColor) {
        const pad = px / 3;
        let maxW = 0;
        for (const ln of lines) maxW = Math.max(maxW, ctx.measureText(ln).width);
        const bx = cx - maxW / 2 - pad;
        const by = firstY - px / 2 - pad;
        const bw = maxW + pad * 2;
        const bh = (lines.length - 1) * lineH + px + pad * 2;
        const radius = Math.min((clip.bgRadius ?? 0) * px, bh / 2);
        ctx.fillStyle = clip.bgColor;
        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') ctx.roundRect(bx, by, bw, bh, radius);
        else ctx.rect(bx, by, bw, bh);
        ctx.fill();
      }

      const offY = (clip.textOffsetY ?? 0) * px;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';
      ctx.lineWidth = Math.max(2, px * 0.12);
      ctx.strokeStyle = clip.outlineColor || '#000000';
      ctx.fillStyle = clip.color || '#ffffff';
      for (let i = 0; i < lines.length; i += 1) {
        const y = firstY + i * lineH + offY;
        ctx.strokeText(lines[i], cx, y);
        ctx.fillText(lines[i], cx, y);
      }
    };

    draw();
    // Redraw once webfonts finish loading so the swatch doesn't stick
    // on a fallback face the first time a font is used.
    if ('fonts' in document) {
      document.fonts.ready.then(() => draw()).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [clip]);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_W}
      height={CANVAS_H}
      className="inspector-text-preview"
      aria-label="Text style preview"
    />
  );
}
